import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { RegistrationSteps } from "@/apps/credit-line/types/registrationSteps";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import { IMCDocService } from "@/apps/credit-line/services/multi-country/multi-country-doc.service";
import { IContractData } from "@/apps/credit-line/models/IContractData";
import percentageFormat from "@/apps/credit-line/helpers/percentageFormatter.helper";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { DocFactoryService } from "@/apps/credit-line/services/multi-country/doc-factory.service";
import { CL_COUNTRY } from "@/shared-models/consumer/Country";
import { getCountryDocs } from "@/apps/credit-line/helpers/multi-country/get-country-docs.helper";
import { LegacyIMCDocService } from "@/apps/credit-line/services/multi-country/legacy-multi-country-doc.service";

export const gtagEventSetUserProps = (): void =>
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.ViewClGenerated,
    "Happy Path",
    "New client - CL generated"
  );

export const registrationAnalyticsSteps = (step: RegistrationSteps): void => {
  switch (step) {
    case RegistrationSteps.OFFER_DISPLAY:
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ViewOfferShown,
        "Happy Path",
        "Offer details"
      );
      break;
    case RegistrationSteps.ACCEPT:
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ViewTyC,
        "Happy Path",
        "View - T&C and Contract"
      );
      break;
    case RegistrationSteps.CONTRACT:
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ViewContractShown,
        "Happy Path",
        "Contract Shown"
      );
      break;
    case RegistrationSteps.FINISHING_PROCESS:
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.ViewClLoading,
        "Happy Path",
        "CL loading"
      );
      break;
    case RegistrationSteps.NOT_APPLICABLE:
    case RegistrationSteps.OFFER_EXPIRED:
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.OfferDeclinedPj,
        "Offer",
        "User declined by Payjoy"
      );
      break;
    default:
      break;
  }
};

export const getContractDate = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = today.getMonth() + 1;
  const dd = today.getDate();

  return dd + "/" + mm + "/" + yyyy;
};

/**
 * Build the contract and Terms & Conditions document service for an offer.
 *
 * This function:
 * - Maps `offer` fields into an `IContractData` payload used by document generators.
 * - When `legacy` is true, delegates to `getCountryDocs` and returns a `LegacyIMCDocService`.
 * - Otherwise, uses `DocFactoryService.create` to return an `IMCDocService<IContractData>`.
 * - Logs and returns `null` on error.
 *
 * @param offer - Offer data used to populate contract placeholders.
 * @param id - Identifier (Device Tag usually) used in error logging to correlate failures.
 * @param legacy - If `true`, use the legacy country-specific document generator (default: `false`).
 * @returns A `LegacyIMCDocService`, `IMCDocService<IContractData>`, or `null` if an error occurred.
 */
export const buildContractAndTyc = (
  offer: IOffer,
  id: string,
  legacy = false
): LegacyIMCDocService | IMCDocService<IContractData> | null => {
  try {
    const offerDataForContract: IContractData = {
      paymentTerm: offer.paymentTerm?.toString(),
      cat: offer.cat?.toString(),
      annualPercentageRate: `${percentageFormat(offer.annualPercentageRate)}`,
      approvedCreditLine: `${offer.approvedCreditLine}`,
      address: offer.address || "",
      currentDate: getContractDate(),
      fullName: offer.fullName || "",
      makeModel: offer.makeModel || "",
      userEmail: offer.userEmail || "",
      taxPercent: offer.taxPercent?.toString() || "16%",
      idNumber: offer?.idNumber,
      annualEffective: offer?.annualEffective,
      monthlyNominalValue: offer?.monthlyNominalValue,
      termDays: offer?.termDays,
      availabilityFeeRate: offer?.availabilityFeeRate,
      insuranceFeeRate: offer?.insuranceFeeRate,
      deviceName: offer?.deviceName,
      interestRatePercent: offer?.interestRatePercent,
      legalCompanySignature: offer?.legalCompanySignature,
      legalRepresentativeID: offer?.legalRepresentativeID,
      legalRepresentative: offer?.legalRepresentative,
      tceaDisplayAmount: offer?.tceaDisplayAmount,
      teaDisplayAmount: offer?.teaDisplayAmount,
      phoneNumber: offer?.phoneNumber,
      idType: offer?.idType,
      availabilityTax: offer?.availabilityTax,
      nationalMaximumRate: offer?.nationalMaximumRate,
      annualPercentageRateDisplay: offer?.annualPercentageRateDisplay,
    };

    if (legacy) {
      return getCountryDocs(offer.country, offerDataForContract);
    }

    return DocFactoryService.create(
      offer.country.toUpperCase() as CL_COUNTRY,
      offerDataForContract
    );
  } catch (error) {
    const errorMsg = `Error building id: ${id} CL contract ->`;
    DatadogLogger.Log(
      errorMsg,
      typeof error === 'object' && error !== null ? error as Record<string, unknown> : undefined,
      LogType.ERROR
    );
    console.log(errorMsg, error);
  }
  return null;
};
